import React, { useContext } from 'react';
import classNames from 'classnames';
import { Combo, ComboDropdownItem } from '@fx-ui/fine-design';
import { Intl } from '../../../constants';
import { filter, find, get, includes, map, join, split, has, pick, some } from '@libs/shared';
import { getPluginDefaultNameById } from '../../../utils';
import { PLUGIN_ARGUMENT_FIELD_TYPE, PLUGIN_ARGUMENT_VALUE_FROM } from '../../../interfaces';
import { PluginComponentContext } from '../plugin-component';

interface FieldVariableSelectProps {
    className?: string;
    value?: string[];
    argumentType: PLUGIN_ARGUMENT_FIELD_TYPE;
    onValueChange?: (value: string[]) => void;
}

/**
 * 渲染插件字段Combo组件的value
 *
 * @param item ComboDropdownItem
 * @param value string|number|null
 */
const renderComboValue = (item?: ComboDropdownItem, value?: string | number | null) => {
    if (!value) {
        return <div className="combo-value" style={{ display: 'block' }} />;
    }
    if (!get(item, 'text')) {
        return (
            <div className="combo-value combo-value-error" style={{ display: 'block' }}>
                {Intl.get('field.deleted')}
            </div>
        );
    }
    return (
        <div title={get(item, 'text')} className="combo-value" style={{ display: 'block' }}>
            {get(item, 'text')}
        </div>
    );
};

const transferValue2ComboValue = (v: { arg?: string[] }) => {
    return v.arg ? join(v.arg, '-') : '';
};

const transferComboValue2Value = (v?: string) => {
    return v ? split(v, '-') : undefined;
};

/**
 * 插件入参-字段选择下拉框
 *
 * @param props
 */
const FieldVariableSelect: React.FC<FieldVariableSelectProps> = (props) => {
    const { className, value, argumentType, onValueChange } = props;

    const pluginComponentStore = useContext(PluginComponentContext);

    if (!pluginComponentStore) {
        return null;
    }

    const {
        optionalFields,
        field,
        checkStatusVisible,
        resolverCtx,
        onParameterFieldSelectorRender,
        onFieldSelect
    } = pluginComponentStore;

    const onChange = onValueChange || pluginComponentStore.onChange;
    const resolverKey = `${resolverCtx}${PLUGIN_ARGUMENT_VALUE_FROM.PROCESS_VAR}`;

    const availableFields = filter(optionalFields, (v) => {
        return includes(v.fields, argumentType) && has(v.resolvers, resolverKey);
    });

    const fieldItems = map(availableFields, (v) => {
        return {
            text: v.title || getPluginDefaultNameById(v.id),
            value: transferValue2ComboValue(get(v.resolvers, resolverKey, { arg: [] }))
        };
    });

    const renderWidgetCombo = () => {
        const { isRequired, isEnabled, fieldType } = field;
        const defaultConfig = {
            onValueChange: (v: string) => {
                onChange(transferComboValue2Value(v));
                if (v) {
                    const selectedField = find(
                        availableFields,
                        (f) =>
                            transferValue2ComboValue(get(f.resolvers, resolverKey, { arg: [] })) ===
                            v
                    );
                    if (selectedField) {
                        onFieldSelect?.(field.id, selectedField.id);
                    }
                }
            },
            disabled: fieldType === PLUGIN_ARGUMENT_FIELD_TYPE.VECTOR ? false : !isEnabled,
            value: transferValue2ComboValue({ arg: value }),
            items: fieldItems,
            hasClear: !isRequired,
            placeholder: Intl.get('select.please'),
            style: { width: '100%' }
        };

        if (onParameterFieldSelectorRender) {
            return (
                <>
                    {onParameterFieldSelectorRender({
                        argumentType,
                        value,
                        onChange,
                        ...pick(defaultConfig, ['disabled', 'hasClear'])
                    })}
                </>
            );
        }

        const isValidValue = some(
            defaultConfig.items,
            (item) => item.value === defaultConfig.value
        );

        const isError = (isRequired && !defaultConfig.value) || !isValidValue;

        return (
            <Combo
                className={classNames('fx-plugin-field-select-combo', className)}
                {...defaultConfig}
                hasSearch={true}
                status={checkStatusVisible && isError ? 'error' : undefined}
                valueRender={renderComboValue}
            />
        );
    };

    return renderWidgetCombo();
};

export default FieldVariableSelect;
